import { hmiCanvasDocumentSchema, HMI_DOCUMENT_KIND, HMI_DOCUMENT_VERSION, type HmiCanvasDocument } from "../schema/hmi-canvas.schema";

export const CURRENT_HMI_DOCUMENT_VERSION = HMI_DOCUMENT_VERSION;

export type HmiDocumentMigration = (doc: unknown) => unknown;

const migrations: Record<number, HmiDocumentMigration> = {
  1: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    const current = doc as Record<string, unknown>;
    const elements = Array.isArray(current.elements)
      ? current.elements.map((element) => {
          if (!element || typeof element !== "object") {
            return element;
          }
          const nextElement = { ...(element as Record<string, unknown>) };
          if (nextElement.type === "trend_chart") {
            const chart = typeof nextElement.chart === "object" && nextElement.chart ? { ...(nextElement.chart as Record<string, unknown>) } : {};
            nextElement.chart = {
              mode: chart.mode ?? "compact",
              showLegend: chart.showLegend ?? false,
              showAxes: chart.showAxes ?? true,
              showGrid: chart.showGrid ?? true,
              lineWidth: chart.lineWidth ?? 3,
            };
          }
          if (nextElement.type === "gauge") {
            const gauge = typeof nextElement.gauge === "object" && nextElement.gauge ? { ...(nextElement.gauge as Record<string, unknown>) } : {};
            nextElement.gauge = {
              min: gauge.min ?? 0,
              max: gauge.max ?? 100,
              unit: gauge.unit,
              showTicks: gauge.showTicks ?? true,
              bands: gauge.bands ?? [
                { from: 0, to: 60, color: "#16a34a" },
                { from: 60, to: 85, color: "#f59e0b" },
                { from: 85, to: 100, color: "#dc2626" },
              ],
            };
          }
          return nextElement;
        })
      : [];
    const assets = Array.isArray(current.assets)
      ? current.assets.map((asset) =>
          asset && typeof asset === "object"
            ? {
                source: "curated",
                ...(asset as Record<string, unknown>),
              }
            : asset,
        )
      : [];
    const bindings = Array.isArray(current.bindings)
      ? current.bindings.map((binding) => {
          if (!binding || typeof binding !== "object") {
            return binding;
          }
          return {
            ...(binding as Record<string, unknown>),
            label:
              typeof (binding as Record<string, unknown>).label === "string"
                ? (binding as Record<string, unknown>).label
                : undefined,
          };
        })
      : [];
    return {
      ...current,
      version: 2,
      elements,
      assets,
      bindings,
    };
  },
  2: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    const current = doc as Record<string, unknown>;
    return {
      ...current,
      version: 3,
    };
  },
  3: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    const current = doc as Record<string, unknown>;
    const metadata = current.metadata && typeof current.metadata === "object" ? { ...(current.metadata as Record<string, unknown>) } : {};
    const workflow = metadata.workflow && typeof metadata.workflow === "object" ? { ...(metadata.workflow as Record<string, unknown>) } : {};
    metadata.workflow = {
      publicationStatus: workflow.publicationStatus === "published" ? "published" : "draft",
      publishedAt: workflow.publishedAt,
      publishedRevisionId: workflow.publishedRevisionId,
      templateId: workflow.templateId,
      templateName: workflow.templateName,
    };
    return {
      ...current,
      version: 4,
      metadata,
    };
  },
  4: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    return {
      ...(doc as Record<string, unknown>),
      version: 5,
    };
  },
  5: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    const current = doc as Record<string, unknown>;
    const elements = Array.isArray(current.elements)
      ? current.elements.map((element) => {
          if (!element || typeof element !== "object") {
            return element;
          }
          const nextElement = { ...(element as Record<string, unknown>) };
          if (nextElement.type === "flow_line") {
            const flow = nextElement.flow && typeof nextElement.flow === "object" ? { ...(nextElement.flow as Record<string, unknown>) } : {};
            nextElement.flow = {
              shape: flow.shape ?? "horizontal",
              direction: flow.direction ?? "forward",
              animate: flow.animate ?? true,
              speed: flow.speed ?? 48,
              stripeLength: flow.stripeLength ?? 18,
              stripeGap: flow.stripeGap ?? 10,
              lineWidth: flow.lineWidth ?? 12,
              turnRatio: flow.turnRatio ?? 0.65,
            };
          }
          return nextElement;
        })
      : current.elements;
    return {
      ...current,
      version: 6,
      elements,
    };
  },
  6: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    const current = doc as Record<string, unknown>;
    const elements = Array.isArray(current.elements)
      ? current.elements.map((element) => {
          if (!element || typeof element !== "object") {
            return element;
          }
          const nextElement = { ...(element as Record<string, unknown>) };
          if (nextElement.type === "flow_line") {
            const flow = nextElement.flow && typeof nextElement.flow === "object" ? { ...(nextElement.flow as Record<string, unknown>) } : {};
            nextElement.flow = {
              shape: flow.shape ?? "horizontal",
              direction: flow.direction ?? "forward",
              animate: flow.animate ?? true,
              speed: flow.speed ?? 48,
              stripeLength: flow.stripeLength ?? 18,
              stripeGap: flow.stripeGap ?? 10,
              stripeTone: flow.stripeTone === "dark" ? "dark" : "light",
              lineWidth: flow.lineWidth ?? 12,
              turnRatio: flow.turnRatio ?? 0.65,
            };
          }
          return nextElement;
        })
      : current.elements;
    return {
      ...current,
      version: 7,
      elements,
    };
  },
  7: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    return {
      ...(doc as Record<string, unknown>),
      version: 8,
    };
  },
  8: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    const current = doc as Record<string, unknown>;
    const elements = Array.isArray(current.elements)
      ? current.elements.map((element) => {
          if (!element || typeof element !== "object") {
            return element;
          }
          const nextElement = { ...(element as Record<string, unknown>) };
          if (nextElement.type === "arrow") {
            const arrow = nextElement.arrow && typeof nextElement.arrow === "object" ? { ...(nextElement.arrow as Record<string, unknown>) } : {};
            nextElement.arrow = {
              direction:
                arrow.direction === "left" || arrow.direction === "up" || arrow.direction === "down"
                  ? arrow.direction
                  : "right",
            };
          }
          return nextElement;
        })
      : current.elements;
    return {
      ...current,
      version: 9,
      elements,
    };
  },
  9: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    const current = doc as Record<string, unknown>;
    const elements = Array.isArray(current.elements)
      ? current.elements.map((element) => {
          if (!element || typeof element !== "object") {
            return element;
          }
          const nextElement = { ...(element as Record<string, unknown>) };
          if (nextElement.type === "gauge") {
            const gauge = nextElement.gauge && typeof nextElement.gauge === "object" ? { ...(nextElement.gauge as Record<string, unknown>) } : {};
            const bands = Array.isArray(gauge.bands) ? gauge.bands : [];
            const firstWarningBand = bands[1] && typeof bands[1] === "object" ? (bands[1] as Record<string, unknown>) : null;
            const firstAlertBand = bands[2] && typeof bands[2] === "object" ? (bands[2] as Record<string, unknown>) : null;
            const min = typeof gauge.min === "number" ? gauge.min : 0;
            const max = typeof gauge.max === "number" ? gauge.max : 100;
            const warningLow =
              typeof gauge.warningLow === "number"
                ? gauge.warningLow
                : typeof firstWarningBand?.from === "number"
                  ? firstWarningBand.from
                  : min + (max - min) * 0.25;
            const warningHigh =
              typeof gauge.warningHigh === "number"
                ? gauge.warningHigh
                : typeof firstAlertBand?.from === "number"
                  ? firstAlertBand.from
                  : min + (max - min) * 0.75;
            nextElement.gauge = {
              min,
              warningLow,
              warningHigh,
              max,
              unit: gauge.unit,
              showTicks: gauge.showTicks ?? true,
            };
          }
          return nextElement;
        })
      : current.elements;
    return {
      ...current,
      version: 10,
      elements,
    };
  },
  10: (doc) => {
    if (!doc || typeof doc !== "object") {
      return doc;
    }
    const current = doc as Record<string, unknown>;
    const elements = Array.isArray(current.elements)
      ? current.elements.map((element) => {
          if (!element || typeof element !== "object") {
            return element;
          }
          const nextElement = { ...(element as Record<string, unknown>) };
          if (nextElement.type === "flow_line") {
            const flow = nextElement.flow && typeof nextElement.flow === "object" ? { ...(nextElement.flow as Record<string, unknown>) } : {};
            const stripeColor =
              typeof flow.stripeColor === "string" && flow.stripeColor.trim()
                ? flow.stripeColor
                : flow.stripeTone === "dark"
                  ? "#0f172a"
                  : "#ffffff";
            delete flow.stripeTone;
            nextElement.flow = {
              shape: flow.shape ?? "horizontal",
              direction: flow.direction ?? "forward",
              animate: flow.animate ?? true,
              speed: flow.speed ?? 48,
              stripeLength: flow.stripeLength ?? 18,
              stripeGap: flow.stripeGap ?? 10,
              stripeColor,
              lineWidth: flow.lineWidth ?? 12,
              turnRatio: flow.turnRatio ?? 0.65,
            };
          }
          return nextElement;
        })
      : current.elements;
    return {
      ...current,
      version: 11,
      elements,
    };
  },
  11: (doc) => doc,
};

function getDocumentVersion(raw: unknown): number {
  if (!raw || typeof raw !== "object") {
    throw new Error("HMI document must be an object.");
  }
  const candidate = raw as { kind?: unknown; version?: unknown };
  if (candidate.kind !== HMI_DOCUMENT_KIND) {
    throw new Error(`Unsupported HMI document kind: ${String(candidate.kind ?? "unknown")}`);
  }
  if (typeof candidate.version !== "number" || !Number.isInteger(candidate.version)) {
    throw new Error("HMI document version must be an integer.");
  }
  return candidate.version;
}

export function migrateHmiDocument(raw: unknown): HmiCanvasDocument {
  const startVersion = getDocumentVersion(raw);
  if (startVersion > CURRENT_HMI_DOCUMENT_VERSION) {
    throw new Error(`Unsupported future HMI document version ${startVersion}.`);
  }

  let current: unknown = raw;
  for (let version = startVersion; version <= CURRENT_HMI_DOCUMENT_VERSION; version += 1) {
    const migration = migrations[version];
    if (!migration) {
      throw new Error(`Missing HMI migration handler for version ${version}.`);
    }
    current = migration(current);
  }

  return hmiCanvasDocumentSchema.parse(current);
}

export function isHmiLayout(layout: unknown): layout is HmiCanvasDocument {
  return Boolean(layout && typeof layout === "object" && "kind" in layout && (layout as { kind?: unknown }).kind === HMI_DOCUMENT_KIND);
}
